import { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQueries } from '@honda-canada/design-system-react';

const deviceMapping = { small: 'Small', large: 'Large' };
const imageTypeMapping = { panoramic: 'Panoramic' };

const useThreeSixty = ({ accessoriesWithOverlayImages, images, assetColor, is360Mode }) => {
  const [imgHeight, setImgHeight] = useState(0);
  const [iconSize, setIconSize] = useState('large');
  const [isImagesLoading, setIsImagesLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const { isMobile, isDesktop } = useMediaQueries();
  const { src: currentImageSrc } = imgRef?.current || {};

  const getDeviceImages = () => {
    let tmp = images?.find?.(image =>
      isMobile ? image.name === deviceMapping.small : image.name === deviceMapping.large,
    );
    if (!tmp) {
      tmp = images?.[isMobile ? deviceMapping.small : deviceMapping.large];
    }
    return tmp?.children || tmp?.assets;
  };

  const getPanoramicImages = () => {
    let tmp = images?.find?.(image => image.name === imageTypeMapping.panoramic);
    if (!tmp) {
      tmp = images?.[imageTypeMapping.panoramic];
    }
    return tmp?.children || tmp?.assets;
  };

  const currentImages = getDeviceImages();
  const firstImage = currentImages?.[0];
  const imageHeight = (firstImage?.Height ?? firstImage?.height)?.value;

  const loadImage = imageSource =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve(true);
      };
      img.onerror = err => {
        reject(err);
      };
      img.src = imageSource;
    });

  // Use a callback to cache this function with the asset color
  const loadAllImages = useCallback(async () => {
    const allImages = getDeviceImages();
    setIsImagesLoading(true);
    try {
      await Promise.all(allImages?.map(({ url }) => loadImage(url)));
      if (accessoriesWithOverlayImages && accessoriesWithOverlayImages.length) {
        await Promise.all(
          accessoriesWithOverlayImages?.map(({ overlayAssets }) => overlayAssets.map(({ src }) => loadImage(src))),
        );
      }
      setIsImagesLoading(false);
    } catch (_) {
      setIsImagesLoading(false);
    }
  }, [assetColor, is360Mode]);

  const onImageLoaded = () => setImageLoaded(true);

  // in case there are images not loaded during the pre load
  // Show the spinner while loading that image
  useEffect(() => {
    if (!currentImageSrc) {
      return;
    }
    if (imgRef?.current?.complete || !isImagesLoading) {
      setImageLoaded(true);
    } else {
      setImageLoaded(false);
    }
  }, [currentImageSrc]);

  useEffect(() => {
    if (imageHeight) {
      setImgHeight(isDesktop ? Number(imageHeight) : 243);
    }
  }, [imageHeight]);

  // Only preload images when 360 is activated
  useEffect(() => {
    if (is360Mode) {
      loadAllImages();
    }
  }, [loadAllImages]);

  useEffect(() => {
    setIconSize(isMobile ? 'default' : 'large');
  }, [isMobile]);

  return {
    deviceImages: getDeviceImages(),
    isImagesLoading,
    getPanoramicImages,
    is360Mode,
    imgRef,
    iconSize,
    imageLoaded,
    onImageLoaded,
    isMobile,
    imgHeight,
  };
};

export default useThreeSixty;
